// src/pages/CategoriesPage.jsx
import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import products from '../data/products';
import './CategoriesPage.css';

const CategoriesPage = () => {
  const navigate = useNavigate();
  const categories = [...new Set(products.map(p => p.category))];
  
  // Function to handle navigation with scroll to top
  const handleCategoryClick = (category) => {
    navigate(`/category/${category}`);
    window.scrollTo({
      top: 0,
      behavior: 'smooth'
    });
  };

  // Function for home breadcrumb
  const handleHomeClick = (e) => {
    e.preventDefault();
    navigate('/');
    window.scrollTo({
      top: 0,
      behavior: 'smooth'
    });
  };

  // Function for contact support
  const handleContactClick = (e) => {
    e.preventDefault();
    navigate('/contact');
    window.scrollTo({
      top: 0,
      behavior: 'smooth'
    });
  };

  const getCategoryStats = (category) => {
    const categoryProducts = products.filter(p => p.category === category);
    const strains = [...new Set(categoryProducts.map(p => p.strain))];
    const minPrice = Math.min(...categoryProducts.map(p => 
      p.priceOptions ? Math.min(...p.priceOptions.map(opt => opt.price)) : p.price
    ));
    const maxPrice = Math.max(...categoryProducts.map(p => 
      p.priceOptions ? Math.max(...p.priceOptions.map(opt => opt.price)) : p.price
    ));

    return {
      count: categoryProducts.length,
      strains: strains.length,
      priceRange: minPrice !== maxPrice ? `$${minPrice} - $${maxPrice}` : `$${minPrice}`
    };
  };

  const getCategoryIcon = (category) => {
    switch(category) {
      case 'Flower':
      case 'Exotic-Flower':
        return '🌿';
      case 'Edibles':
        return '🍬';
      case 'Concentrates':
        return '⚗️';
      case 'cart':
        return '🔋';
      case 'disposable-cart':
        return '💨';
      case 'Pre-rolls':
        return '🚬';
      case 'Shake':
        return '🍃';
      default:
        return '📦';
    }
  };

  const getCategoryColor = (category) => {
    switch(category) {
      case 'Flower':
      case 'Exotic-Flower':
        return 'var(--color-flower)';
      case 'Edibles':
        return 'var(--color-edibles)';
      case 'Concentrates':
        return 'var(--color-concentrates)';
      case 'cart':
      case 'disposable-cart':
        return 'var(--color-cart)';
      case 'Pre-rolls':
        return 'var(--color-prerolls)';
      case 'Shake':
        return 'var(--color-shake)';
      default:
        return 'var(--color-default)';
    }
  };

  return (
    <div className="categories-page">
      <div className="categories-container">
        <header className="categories-header">
          <div className="header-content">
            <div className="breadcrumb">
              <button 
                onClick={handleHomeClick}
                className="breadcrumb-link"
                style={{
                  background: 'none',
                  border: 'none',
                  padding: 0,
                  color: 'var(--text-light)',
                  textDecoration: 'none',
                  cursor: 'pointer',
                  fontFamily: 'inherit',
                  fontSize: 'inherit'
                }}
                onMouseEnter={(e) => e.target.style.color = 'var(--primary)'}
                onMouseLeave={(e) => e.target.style.color = 'var(--text-light)'}
              >
                Home
              </button>
              <span className="breadcrumb-separator">/</span>
              <span className="breadcrumb-current">Categories</span>
            </div>
            <h1 className="categories-title">Product Categories</h1>
            <p className="categories-subtitle">Explore our curated collection of premium products</p>
          </div>
          <div className="header-stats">
            <div className="stat-box">
              <div className="stat-number">{categories.length}</div>
              <div className="stat-label">Categories</div>
            </div>
            <div className="stat-box">
              <div className="stat-number">{products.length}</div>
              <div className="stat-label">Total Products</div>
            </div>
          </div>
        </header>

        <div className="categories-grid">
          {categories.map((category, index) => {
            const stats = getCategoryStats(category);
            const icon = getCategoryIcon(category);
            const color = getCategoryColor(category);
            
            return (
              <button
                key={category}
                onClick={() => handleCategoryClick(category)}
                className="category-card"
                style={{ 
                  '--card-color': color,
                  cursor: 'pointer',
                  border: 'none',
                  background: 'white',
                  borderRadius: 'var(--radius-lg)',
                  padding: 'var(--space-xl)',
                  boxShadow: 'var(--shadow-sm)',
                  transition: 'all 0.3s ease',
                  position: 'relative',
                  overflow: 'hidden',
                  textAlign: 'left',
                  fontFamily: 'inherit',
                  fontSize: 'inherit',
                  display: 'block',
                  width: '100%'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = 'translateY(-8px)';
                  e.currentTarget.style.boxShadow = 'var(--shadow-lg)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.boxShadow = 'var(--shadow-sm)';
                }}
                data-delay={index * 0.05}
              >
                <div className="card-corner">
                  <div className="corner-decoration"></div>
                </div>
                <div className="card-header">
                  <div className="category-icon-wrapper">
                    <div className="icon-backdrop"></div>
                    <span className="category-icon">{icon}</span>
                  </div>
                  <span className="arrow-indicator">
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                      <path d="M5 12h14M12 5l7 7-7 7"/>
                    </svg>
                  </span>
                </div>
                
                <div className="card-content">
                  <h3 className="category-name">
                    {category.replace('-', ' ')}
                  </h3>
                  
                  <div className="category-stats">
                    <div className="stat-item">
                      <div className="stat-icon-wrapper">
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                          <path d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4"/>
                        </svg>
                      </div>
                      <div className="stat-text">
                        <div className="stat-value">{stats.count}</div>
                        <div className="stat-label">Products</div>
                      </div>
                    </div>
                    
                    <div className="stat-item">
                      <div className="stat-icon-wrapper">
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                          <path d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"/>
                        </svg>
                      </div>
                      <div className="stat-text">
                        <div className="stat-value">{stats.strains}</div>
                        <div className="stat-label">Strains</div>
                      </div>
                    </div>
                    
                    <div className="stat-item">
                      <div className="stat-icon-wrapper">
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                          <path d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z"/>
                        </svg>
                      </div>
                      <div className="stat-text">
                        <div className="stat-value">{stats.priceRange}</div>
                        <div className="stat-label">Price Range</div>
                      </div>
                    </div>
                  </div>
                </div>
                
                <div className="card-footer">
                  <div className="cta-button">
                    <span>Browse Category</span>
                    <div className="cta-arrow">
                      <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5">
                        <path d="M3 8h10m-5 5l5-5-5-5"/>
                      </svg>
                    </div>
                  </div>
                </div>
              </button>
            );
          })}
        </div>

        <footer className="categories-footer">
          <div className="footer-content">
            <div className="footer-text">
              <h3>Can't find what you're looking for?</h3>
              <p>Contact our team for personalized recommendations</p>
            </div>
            <button
              onClick={handleContactClick}
              className="footer-cta"
              style={{
                cursor: 'pointer',
                background: 'none',
                border: '2px solid var(--primary)',
                borderRadius: 'var(--radius-lg)',
                padding: 'var(--space-md) var(--space-xl)',
                color: 'var(--primary)',
                fontWeight: '600',
                fontSize: '1rem',
                transition: 'all 0.2s ease',
                fontFamily: 'inherit',
                textDecoration: 'none',
                display: 'inline-flex',
                alignItems: 'center',
                gap: '8px'
              }}
              onMouseEnter={(e) => {
                e.target.style.backgroundColor = 'var(--primary)';
                e.target.style.color = 'white';
              }}
              onMouseLeave={(e) => {
                e.target.style.backgroundColor = 'transparent';
                e.target.style.color = 'var(--primary)';
              }}
            >
              Contact Support
            </button>
          </div>
        </footer>
      </div>
    </div>
  );
};

export default CategoriesPage;